import React, { useState } from 'react';

import { Alert, Button, Card, Col, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faDownload, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';

import { bootstrapSecondary, grape, lightGrayBorder } from 'lib/css/colors';
import {
  INTERNAL_TOOLS_GENERATE_ENGAGED_MANUFACTURERS_GMV_REPORT,
  INTERNAL_TOOLS_GENERATE_DISTRIBUTORS_GMV_REPORT,
  INTERNAL_TOOLS_GENERATE_MANUFACTURER_ENGAGEMENT_REPORT,
} from 'lib/networking/endpoints';
import { httpPost } from 'lib/networking/http';

// Available reports
const AVAILABLE_REPORTS = [
  {
    id: 'engaged-manufacturers-gmv',
    title: 'Engaged Manufacturers with GMV',
    description:
      'Generate a report of all engaged manufacturers with their annualized GMV data from Snowflake',
    icon: faFileAlt,
    endpoint: INTERNAL_TOOLS_GENERATE_ENGAGED_MANUFACTURERS_GMV_REPORT,
  },
  {
    id: 'distributors-gmv',
    title: 'Distributors with GMV',
    description:
      'Generate a report of all distributors with their annualized GMV data, unique product counts, and verification status',
    icon: faFileAlt,
    endpoint: INTERNAL_TOOLS_GENERATE_DISTRIBUTORS_GMV_REPORT,
  },
  {
    id: 'manufacturer-engagement',
    title: 'Manufacturer Engagement Report',
    description:
      'Generate a report of engaged manufacturers with engagement date, source (GDSN or Change Log), and user email for change log engagements',
    icon: faFileAlt,
    endpoint: INTERNAL_TOOLS_GENERATE_MANUFACTURER_ENGAGEMENT_REPORT,
  },
];

function ReportsDownload() {
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'info' });
  const [generatingReportId, setGeneratingReportId] = useState(null);

  const handleGenerateReport = report => {
    Swal.fire({
      title: 'Generate Report?',
      html: `
        <p>Are you sure you want to generate the following report?</p>
        <p><strong>${report.title}</strong></p>
        <p class="text-muted">${report.description}</p>
        <p>
        The report will be generated asynchronously and may take several
        minutes depending on the data size.
        </p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, generate report',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        setGeneratingReportId(report.id);

        // Call the endpoint directly
        httpPost(report.endpoint, {})
          .then(res => {
            const result = res?.data?.data;
            if (result) {
              Swal.fire({
                title: 'Report Queued',
                html: `
                  <p>${result.message}</p>
                  <p>The report generation task has been queued and will be processed shortly.
                  Once complete, you can download the generated file from the
                  <a href="/file-exports" target="_blank" rel="noopener noreferrer">
                    File Exports Page</a>.</p>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
              });
            }
            setGeneratingReportId(null);
          })
          .catch(error => {
            const errorMsg =
              error?.response?.data?.error || 'Failed to queue report generation task';
            Swal.fire({
              title: 'Error',
              text: errorMsg,
              icon: 'error',
              confirmButtonText: 'OK',
            });
            setGeneratingReportId(null);
          });
      }
    });
  };

  return (
    <>
      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <Card className={css(styles.card)}>
        <Card.Body>
          <p className={css(styles.description)}>
            Generate and download various reports. Once a report is generated, you can access it
            from the{' '}
            <a href="/file-exports" target="_blank" rel="noopener noreferrer">
              File Exports page
            </a>
            .
          </p>
          <hr />

          <Row>
            {AVAILABLE_REPORTS.map(report => (
              <Col md={6} key={report.id}>
                <Card className={css(styles.reportCard)}>
                  <Card.Body>
                    <div className={css(styles.reportHeader)}>
                      <FontAwesomeIcon icon={report.icon} className={css(styles.reportIcon)} />
                      <div className={css(styles.reportContent)}>
                        <h5 className={css(styles.reportTitle)}>{report.title}</h5>
                        <p className={css(styles.reportDescription)}>{report.description}</p>
                      </div>
                    </div>
                    <div className={css(styles.reportActions)}>
                      <Button
                        variant="primary"
                        onClick={() => handleGenerateReport(report)}
                        disabled={generatingReportId === report.id}
                        className={css(styles.generateButton)}
                      >
                        {generatingReportId === report.id ? (
                          <>Generating...</>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faDownload} /> Generate Report
                          </>
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {AVAILABLE_REPORTS.length === 0 && (
            <Alert variant="info" className={css(styles.noReportsAlert)}>
              No reports are currently available.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: '1.5rem',
    padding: '1.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  description: {
    fontSize: '0.95rem',
    color: bootstrapSecondary,
    marginBottom: '1rem',
  },
  reportCard: {
    marginBottom: '1.5rem',
    border: `1px solid ${lightGrayBorder}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
    },
  },
  reportHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    marginBottom: '1rem',
  },
  reportIcon: {
    fontSize: '2rem',
    color: grape,
    marginTop: '0.25rem',
  },
  reportContent: {
    flex: 1,
  },
  reportTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
    color: '#333',
  },
  reportDescription: {
    fontSize: '0.9rem',
    color: bootstrapSecondary,
    marginBottom: 0,
    lineHeight: '1.5',
  },
  reportActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem',
  },
  generateButton: {
    minWidth: '150px',
  },
  noReportsAlert: {
    marginTop: '2rem',
    textAlign: 'center',
  },
});

export default ReportsDownload;
